// CSR-953 — Shared, pure derivation that splits the Physical-Damage-covered
// vehicle set into one entry per distinct (comprehensive, collision) deductible
// pair. Built ON TOP OF `getPhysicalDamageVehicles` (it partitions that helper's
// returned covered lists, never re-deriving coverage) so the CSR-1144 curated
// selection, removed-asset, and Non-Owned-Attached-Trailer exclusions are
// inherited for free and the tier split can never disagree with the covered set.
//
// Source of truth: per-vehicle `deductibleComp` / `deductibleCollision` read via
// `getVehicleDeductiblePair` (same `pickPdNumber` source as the COI). The
// `coverages.physicalDamage` block is a FALLBACK ONLY: covered vehicles carrying
// no per-vehicle deductible fold into a single tier sourced from that block —
// never rendered as a "(none/none)" split tier.
//
// Nothing here is stored or round-tripped to DC; tiers are computed on read.

import type {
  PhysicalDamageVehicleSelection,
  PolicyCoverages,
  PolicyVehicle,
} from "./schema";
import { getPhysicalDamageVehicles } from "./physical-damage-vehicles";
import { getVehicleDeductiblePair } from "./coi-vehicles";

export interface PhysicalDamageTier {
  comprehensive: number | null;
  collision: number | null;
  powerUnits: PolicyVehicle[];
  trailers: PolicyVehicle[];
}

export interface PhysicalDamageTierResult {
  // Ordered by descending comprehensive then descending collision so the
  // highest tier renders first (matches the "highest" emphasis on the COI).
  tiers: PhysicalDamageTier[];
  // True when no PD-covered vehicle carries per-vehicle deductibles, so the
  // single tier was sourced from `coverages.physicalDamage` — today's behaviour.
  fallback: boolean;
}

type TierInput = {
  vehicles?: PolicyVehicle[] | null;
  trailers?: PolicyVehicle[] | null;
  coverages?: PolicyCoverages | null;
  physicalDamageVehicles?: PhysicalDamageVehicleSelection | null;
};

function parseBlockDeductible(raw: string | undefined): number | null {
  if (raw == null) return null;
  const cleaned = String(raw).replace(/[^\d.-]/g, "");
  if (cleaned === "") return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function fallbackPair(coverages: PolicyCoverages | null | undefined): {
  comprehensive: number | null;
  collision: number | null;
} {
  const block = coverages?.physicalDamage;
  return {
    comprehensive: parseBlockDeductible(block?.comprehensiveDeductible),
    collision: parseBlockDeductible(block?.collisionDeductible),
  };
}

function tierKey(comp: number | null, coll: number | null): string {
  return `${comp ?? "none"}|${coll ?? "none"}`;
}

// Descending comprehensive then descending collision; nulls sort last.
function compareTiersDesc(a: PhysicalDamageTier, b: PhysicalDamageTier): number {
  const byComp = (b.comprehensive ?? -Infinity) - (a.comprehensive ?? -Infinity);
  if (byComp !== 0) return byComp;
  return (b.collision ?? -Infinity) - (a.collision ?? -Infinity);
}

export function getPhysicalDamageTiers(policy: TierInput): PhysicalDamageTierResult {
  const { powerUnits, trailers } = getPhysicalDamageVehicles({
    vehicles: policy.vehicles,
    trailers: policy.trailers,
    coverages: policy.coverages,
    physicalDamageVehicles: policy.physicalDamageVehicles,
  });

  const fallback = fallbackPair(policy.coverages);
  const groups = new Map<string, PhysicalDamageTier>();

  const place = (v: PolicyVehicle, list: "powerUnits" | "trailers") => {
    const pair = getVehicleDeductiblePair(v);
    // A covered vehicle with no per-vehicle deductible folds into the
    // `coverages.physicalDamage` fallback tier (Maestro locked decision):
    // never a visible "(none/none)" split.
    const comp = pair.comprehensive ?? fallback.comprehensive;
    const coll = pair.collision ?? fallback.collision;
    const key = tierKey(comp, coll);
    let tier = groups.get(key);
    if (!tier) {
      tier = { comprehensive: comp, collision: coll, powerUnits: [], trailers: [] };
      groups.set(key, tier);
    }
    tier[list].push(v);
  };

  for (const v of powerUnits.covered) place(v, "powerUnits");
  for (const v of trailers.covered) place(v, "trailers");

  // No covered vehicle yields any per-vehicle deductible → render a single tier
  // sourced from `coverages.physicalDamage` (today's behaviour). This covers
  // both the no-covered-vehicles case and the all-`(null,null)` curated case.
  const hasPerVehicleData = powerUnits.covered
    .concat(trailers.covered)
    .some((v) => {
      const pair = getVehicleDeductiblePair(v);
      return pair.comprehensive !== null || pair.collision !== null;
    });

  if (!hasPerVehicleData) {
    const folded = groups.get(tierKey(fallback.comprehensive, fallback.collision));
    return {
      tiers: [
        {
          comprehensive: fallback.comprehensive,
          collision: fallback.collision,
          powerUnits: folded?.powerUnits ?? [],
          trailers: folded?.trailers ?? [],
        },
      ],
      fallback: true,
    };
  }

  const tiers = Array.from(groups.values()).sort(compareTiersDesc);
  return { tiers, fallback: false };
}

// Independent max of comprehensive and max of collision across PD-covered
// vehicles — the max comp and max coll may come from different vehicles (this
// is NOT "the tier with the highest comp"). Returns `{ null, null }` when no
// covered vehicle carries any per-vehicle deductible, signalling the COI caller
// to fall back to the stored `coverages.physicalDamage` block.
export function getMaxPhysicalDamageDeductibles(policy: TierInput): {
  comprehensive: number | null;
  collision: number | null;
} {
  const { powerUnits, trailers } = getPhysicalDamageVehicles({
    vehicles: policy.vehicles,
    trailers: policy.trailers,
    coverages: policy.coverages,
    physicalDamageVehicles: policy.physicalDamageVehicles,
  });

  let maxComp: number | null = null;
  let maxColl: number | null = null;
  for (const v of [...powerUnits.covered, ...trailers.covered]) {
    const { comprehensive, collision } = getVehicleDeductiblePair(v);
    if (comprehensive !== null) maxComp = maxComp === null ? comprehensive : Math.max(maxComp, comprehensive);
    if (collision !== null) maxColl = maxColl === null ? collision : Math.max(maxColl, collision);
  }
  return { comprehensive: maxComp, collision: maxColl };
}
